import { useState, useEffect, useCallback, useMemo } from "react";
import type {
  RepeatPriceCalibration,
  RepeatPriceThresholds,
} from "@shared-core/data/price-precedent-types";
import { useParams } from "react-router";
import { toast } from "sonner";
import { paths } from "@shared-core/routing/routes";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import {
  rebateBreakdown,
  orderRebateBands,
  capacityAreasFor,
  CAPACITY_AREAS,
} from "@shared-core/mock-data/pricing-quote";
import { priceForQuantity } from "@shared-core/domain/catalogue-pricing";
import { formatCurrencyGbp } from "@shared-core/domain/formatters";
import type { CatalogueItem } from "@shared-core/data/catalogue-types";
import type { SpeedUplift } from "@shared-core/data/speed-types";
import type {
  GuardrailRow,
  PrecedentConfig,
  FactorKey,
  PriceGroup,
  Stance,
  StanceSetting,
  CommitmentConfig,
  RebateBand,
  CapacityBand,
} from "@shared-core/data/pricing-admin-types";
import type {
  PriceQuoteRequest,
  QuoteLine,
  PriceQuote,
} from "@shared-core/data/pricing-quote";
import { Button } from "../app/components/primitives/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardAction,
  CardContent,
} from "../app/components/primitives/card";
import { Badge } from "../app/components/primitives/badge";
import { Input } from "../app/components/primitives/input";
import { Label } from "../app/components/primitives/label";
import { Switch } from "../app/components/primitives/switch";
import { Separator } from "../app/components/primitives/separator";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../app/components/primitives/select";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../app/components/primitives/accordion";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../app/components/primitives/table";
import { Tabs, TabsList, TabsTrigger } from "../app/components/primitives/tabs";
import { EditorHeader } from "../app/components/patterns/EditorHeader";
import { UnsavedChangesGuard } from "../app/components/patterns/UnsavedChangesGuard";
import { cn } from "../app/components/primitives/utils";
import {
  Coins,
  Clock,
  BadgePercent,
  TrendingUp,
  Gauge,
  ShieldCheck,
  Plus,
  Trash2,
  Calculator,
  Truck,
  History,
} from "lucide-react";

// Settings → Pricing — the price-group editor. A scope switch, the version header,
// the editable factor panels, and a worked example that prices a real catalogue
// item through the saved settings + a scenario, showing the full derivation.
// Built on the standard primitives + settings tokens.

const FACTORS: { key: FactorKey; label: string; icon: typeof Clock }[] = [
  { key: "commercial", label: "Base", icon: Coins },
  { key: "precedent", label: "Precedent", icon: History },
  { key: "speed", label: "Speed", icon: Clock },
  { key: "commitment", label: "Commitment", icon: BadgePercent },
  { key: "market", label: "Market", icon: TrendingUp },
  { key: "capacity", label: "Capacity", icon: Gauge },
  { key: "guardrails", label: "Guardrails", icon: ShieldCheck },
  { key: "carriage", label: "Carriage", icon: Truck },
];

const STANCE_LABEL: Record<Stance, string> = {
  hold: "Neutral",
  win: "Win it",
  track: "Track market",
  premium: "Premium",
  optimise: "Maximise",
};

const VERDICT: Record<
  "ok" | "approval" | "blocked",
  { label: string; variant: "success" | "warning" | "error" }
> = {
  ok: { label: "✓ within limits", variant: "success" },
  approval: { label: "⚠ approval", variant: "warning" },
  blocked: { label: "✕ blocked", variant: "error" },
};

const microLabel =
  "text-[11px] font-medium uppercase tracking-wide text-muted-foreground";

// Illustrative live loads per area for the worked example (scenario inputs).
const DEFAULT_CAP_LOADS: Record<string, number> = {
  Litho: 0.52,
  Digital: 0.78,
  Finishing: 0.95,
  "Large format": 0.4,
};

export function SettingsPricingPage() {
  const { groupId } = useParams<{ groupId: string }>();
  const adapter = useDataAdapter();
  const [priceGroup, setPriceGroup] = useState<PriceGroup | null>(null);
  const [catalogueItems, setCatalogueItems] = useState<CatalogueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    setError(false);
    try {
      const group = await adapter.getPriceGroup(groupId);
      setPriceGroup(group);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [adapter, groupId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    // Best-effort: only feeds the Worked example panel's item picker. A
    // failure here leaves that panel with nothing to price — a visible,
    // honest empty state — rather than misstating the price group itself,
    // so it doesn't need its own error/retry UI.
    adapter
      .listCataloguesForManagement()
      .then((catalogues) => {
        const coreRange = catalogues.find((c) => c.audience === "core_range");
        if (!coreRange) return;
        return adapter.getCatalogueWithItems(coreRange.id).then((view) => {
          setCatalogueItems(view?.items ?? []);
        });
      })
      .catch(() => {});
  }, [adapter]);

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  }
  if (error || !priceGroup) {
    return (
      <div className="p-6 space-y-2">
        <p className="text-sm font-medium">Price group could not be loaded.</p>
        <Button variant="outline" size="sm" onClick={() => void load()}>
          Retry
        </Button>
      </div>
    );
  }

  // Keyed by group so the editable working copy resets on load/switch.
  return (
    <PricingEditor
      key={priceGroup.id}
      priceGroup={priceGroup}
      catalogueItems={catalogueItems}
      onRefresh={load}
    />
  );
}

// ── Editor: working copy + header save/revert + factor panels + worked example ─

function PricingEditor({
  priceGroup,
  catalogueItems,
  onRefresh,
}: {
  priceGroup: PriceGroup;
  catalogueItems: CatalogueItem[];
  onRefresh: () => void;
}) {
  const adapter = useDataAdapter();
  const [draft, setDraft] = useState<PriceGroup>(priceGroup);
  const [dirty, setDirty] = useState(false);
  const edit = (next: PriceGroup) => {
    setDraft(next);
    setDirty(true);
  };
  const subtitle =
    priceGroup.scope === "central"
      ? "The shared price group, used for customers without their own."
      : `${priceGroup.scope.customerName}'s own price group.`;

  const handleSave = async () => {
    try {
      await adapter.savePricingVersion(priceGroup.id, draft);
      setDirty(false);
      toast.success("Saved — live now");
      onRefresh();
    } catch {
      toast.error("Failed to save — please try again");
    }
  };

  const handleRevert = () => {
    setDraft(priceGroup);
    setDirty(false);
    toast.info("Reverted to the saved version");
  };

  return (
    <div className="space-y-6">
      <UnsavedChangesGuard when={dirty} />
      <EditorHeader
        backTo={paths.settingsPricing()}
        backLabel="Price groups"
        name={priceGroup.scope === "central" ? "Standard" : priceGroup.name}
        subtitle={subtitle}
        versions={priceGroup.versions}
        dirty={dirty}
        onSave={handleSave}
        onRevert={handleRevert}
      />
      <PriceGroupWorkbench
        draft={draft}
        catalogueItems={catalogueItems}
        onEdit={edit}
      />
    </div>
  );
}

function PriceGroupWorkbench({
  draft,
  catalogueItems,
  onEdit,
}: {
  draft: PriceGroup;
  catalogueItems: CatalogueItem[];
  onEdit: (d: PriceGroup) => void;
}) {
  const [factor, setFactor] = useState<FactorKey>("commercial");

  return (
    <>
      <Tabs value={factor} onValueChange={(v) => setFactor(v as FactorKey)}>
        <TabsList>
          {FACTORS.map((f) => {
            const Icon = f.icon;
            return (
              <TabsTrigger key={f.key} value={f.key}>
                <Icon className="size-4" />
                {f.label}
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>

      <div className="grid gap-6 lg:grid-cols-3 items-start">
        <div className="lg:col-span-2">
          {factor === "commercial" && (
            <CommercialPanel draft={draft} onEdit={onEdit} />
          )}
          {factor === "speed" && (
            <SpeedPriceRulePanel draft={draft} onEdit={onEdit} />
          )}
          {factor === "commitment" && (
            <CommitmentPanel
              draft={draft}
              catalogueItems={catalogueItems}
              onEdit={onEdit}
            />
          )}
          {factor === "market" && (
            <MarketPanel
              draft={draft}
              catalogueItems={catalogueItems}
              onEdit={onEdit}
            />
          )}
          {factor === "capacity" && (
            <CapacityPanel draft={draft} onEdit={onEdit} />
          )}
          {factor === "precedent" && (
            <PrecedentPanel draft={draft} onEdit={onEdit} />
          )}
          {factor === "guardrails" && (
            <GuardrailsPanel draft={draft} onEdit={onEdit} />
          )}
          {factor === "carriage" && (
            <CarriagePanel draft={draft} onEdit={onEdit} />
          )}
        </div>
        <WorkedExample
          draft={draft}
          factor={factor}
          catalogueItems={catalogueItems}
        />
      </div>
    </>
  );
}

// ── Worked example — a row-by-row walkthrough of the working copy ────────────
// Each stage is an accordion item carrying its own scenario inputs + the
// adjustment it produces, with a running total. The stage matching the active
// factor tab opens by default; collapsed, it reads cost-plus → rules → price.

function WorkedExample({
  draft,
  factor,
  catalogueItems,
}: {
  draft: PriceGroup;
  factor: FactorKey;
  catalogueItems: CatalogueItem[];
}) {
  const adapter = useDataAdapter();
  const [itemId, setItemId] = useState("");
  const item = catalogueItems.find((it) => it.id === itemId) ?? null;
  const baseOf = (id: string, q: number) => {
    const it = catalogueItems.find((x) => x.id === id);
    return it && it.quantityBreaks.length
      ? priceForQuantity(it.quantityBreaks, q).price
      : 0;
  };
  const [qty, setQty] = useState<number>(0);
  const [speedCode, setSpeedCode] = useState(draft.speed.baselineCode);
  const [competitor, setCompetitor] = useState<number | undefined>(undefined);
  const [spend, setSpend] = useState<number | undefined>(600000);
  const [capacityLoad, setCapacityLoad] = useState<Record<string, number>>(
    () => ({ ...DEFAULT_CAP_LOADS }),
  );
  // Total base cost (the Tharstern job cost), the way it's normally thought about.
  const [baseCost, setBaseCost] = useState<number | undefined>(undefined);
  const [prior, setPrior] = useState<number | undefined>(undefined);
  const [open, setOpen] = useState<FactorKey | null>(factor);
  const [quote, setQuote] = useState<PriceQuote | null>(null);
  useEffect(() => setOpen(factor), [factor]);

  // Initialise scenario inputs when catalogue items first arrive.
  useEffect(() => {
    if (catalogueItems.length > 0 && !itemId) {
      const first = catalogueItems[0];
      const q = first.defaultQuantity;
      const b = first.quantityBreaks.length
        ? priceForQuantity(first.quantityBreaks, q).price
        : 0;
      setItemId(first.id);
      setQty(q);
      setCompetitor(Math.round(b * 1.08));
      setBaseCost(Math.round(b * 0.6));
    }
  }, [catalogueItems]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectItem = (id: string) => {
    const it = catalogueItems.find((x) => x.id === id);
    const q = it?.defaultQuantity ?? 0;
    const b =
      it && it.quantityBreaks.length
        ? priceForQuantity(it.quantityBreaks, q).price
        : 0;
    setItemId(id);
    setQty(q);
    setSpeedCode(draft.speed.baselineCode);
    setCompetitor(Math.round(b * 1.08));
    setBaseCost(Math.round(b * 0.6));
  };

  // Stable primitive dep for the item's quantity breaks — changes when itemId changes.
  const quantityBreaksJson = JSON.stringify(
    (item?.quantityBreaks ?? []).map((b) => ({
      qty: b.qty,
      pricePence: Math.round(b.price * 100),
    })),
  );

  // Fetch the quote from the real API whenever any input changes.
  // Debounced 300ms so rapid text-box changes don't fire on every keystroke.
  // The API uses the saved price group — unsaved draft changes are not reflected.
  useEffect(() => {
    if (!itemId || !qty || !draft.id) return;
    const req: PriceQuoteRequest = {
      itemId,
      quantity: qty,
      optionDeltas: [],
      speedCode,
      priceGroupId: draft.id,
      quantityBreaks: JSON.parse(quantityBreaksJson) as {
        qty: number;
        pricePence: number;
      }[],
      scenario: {
        competitorPrice: competitor,
        spendToDate: spend,
        capacityLoad,
        unitCost: baseCost != null && qty > 0 ? baseCost / qty : undefined,
        priorPrice: prior,
      },
    };
    let cancelled = false;
    const timer = setTimeout(() => {
      adapter
        .getPriceQuote(itemId, req)
        .then((q: PriceQuote) => {
          if (!cancelled) setQuote(q);
        })
        // Fire-and-forget re-quote on every scenario-input change: a failure
        // just leaves `quote` at its previous value, and the Final price
        // section already renders "—" when there's no quote — a visible,
        // honest state, not a false one — so no dedicated error UI here.
        .catch(() => {});
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    adapter,
    itemId,
    qty,
    speedCode,
    draft.id,
    quantityBreaksJson,
    competitor,
    spend,
    capacityLoad,
    baseCost,
    prior,
  ]);

  const base = baseOf(itemId, qty);
  const rawCost = baseCost ?? null;
  const lineFor = (src: string) => quote?.lines.find((l) => l.source === src);
  const speedLine = lineFor("speed");
  const commitLine = lineFor("commitment");
  const rebateLine = lineFor("rebate");
  const marketLine = lineFor("market");
  const capLine = lineFor("capacity");

  // Running total in display order — applied lines only, so suggested lines
  // visibly don't move the price.
  const NET_SOURCES: Record<FactorKey, string[]> = {
    commercial: ["base", "option"],
    // Precedent is a level, not a factor line — it replaces the built price
    // rather than moving it, so nothing in the worked example flows from it.
    precedent: [],
    speed: ["speed"],
    commitment: ["commitment", "rebate"],
    market: ["market"],
    capacity: ["capacity"],
    guardrails: [],
    // Carriage never contributes to the print price — it isn't rendered in
    // this sandbox at all (see the FACTORS filter below), but every FactorKey
    // needs an entry here to satisfy the Record type.
    carriage: [],
  };
  const factorNet = (k: FactorKey) =>
    (quote?.lines ?? [])
      .filter(
        (l) => NET_SOURCES[k].includes(l.source ?? "") && l.applied !== false,
      )
      .reduce((s, l) => s + (l.kind === "discount" ? -l.amount : l.amount), 0);
  const order: FactorKey[] = [
    "commercial",
    "speed",
    "commitment",
    "market",
    "capacity",
  ];
  let run = 0;
  const runAfter = {} as Record<FactorKey, number>;
  for (const k of order) {
    run += factorNet(k);
    runAfter[k] = run;
  }
  runAfter.guardrails = run;
  runAfter.precedent = runAfter.commercial;

  const delta = (l?: QuoteLine) =>
    l
      ? `${l.kind === "discount" ? "−" : "+"}${formatCurrencyGbp(l.amount)}${l.applied === false ? " sugg." : ""}`
      : null;

  const summary = (key: FactorKey): React.ReactNode => {
    if (key === "commercial")
      return (
        <span className="font-medium tabular-nums">
          {formatCurrencyGbp(base)}
        </span>
      );
    if (key === "guardrails")
      return quote?.verdict ? (
        <span
          className={
            VERDICT[quote.verdict.status].variant === "success"
              ? "text-green-700"
              : "text-amber-700"
          }
        >
          {VERDICT[quote.verdict.status].label}
        </span>
      ) : (
        <span>—</span>
      );
    const d = delta(lineFor(NET_SOURCES[key][0]));
    return (
      <span className="flex items-center gap-1.5">
        <span>{d ?? "—"}</span>
        <span className="text-muted-foreground/60">→</span>
        <span className="font-medium tabular-nums text-foreground">
          {formatCurrencyGbp(runAfter[key])}
        </span>
      </span>
    );
  };

  return (
    <Card className="gap-0 lg:sticky lg:top-6">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-base">
          <Calculator className="size-4 text-primary" />
          Worked example
        </CardTitle>
        <CardDescription>Priced through saved settings.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Spec — what we're pricing. */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">Pricing</span>
          <Choice
            value={itemId}
            onChange={selectItem}
            options={catalogueItems.map((it) => ({
              value: it.id,
              label: it.name,
            }))}
            ariaLabel="Item"
          />
          <Choice
            value={String(qty)}
            onChange={(v) => setQty(Number(v))}
            options={(item?.quantityBreaks ?? []).map((b) => ({
              value: String(b.qty),
              label: b.qty.toLocaleString(),
            }))}
            ariaLabel="Quantity"
          />
        </div>

        {/* cost-plus → rules → final price */}
        <Accordion
          type="single"
          collapsible
          value={open ?? ""}
          onValueChange={(v) => setOpen((v || null) as FactorKey | null)}
          className="space-y-1.5"
        >
          {/* Carriage never joins this stack — it's address-gated, priced
              outside the print price, and config-only on its own tab. */}
          {/* Carriage sits outside the print price; precedent is a level that
              replaces the built price rather than a factor that moves it, so
              neither has a line in the derivation. */}
          {FACTORS.filter(
            (f) => f.key !== "carriage" && f.key !== "precedent",
          ).map((f) => {
            const Icon = f.icon;
            return (
              <AccordionItem
                key={f.key}
                value={f.key}
                className="rounded-md border data-[state=open]:border-primary/40 data-[state=open]:bg-muted/30"
              >
                <AccordionTrigger className="gap-2 px-3 py-2.5 hover:no-underline">
                  <span className="flex flex-1 items-center justify-between gap-2 text-sm">
                    <span className="flex items-center gap-2">
                      <Icon className="size-4 text-muted-foreground" />
                      <span className="font-medium">{f.label}</span>
                    </span>
                    <span className="text-muted-foreground">
                      {summary(f.key)}
                    </span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="space-y-3 px-3">
                  {f.key === "commercial" && (
                    <FactorBody
                      scenario={
                        <ScnField label="Base cost">
                          <MoneyInput value={baseCost} onChange={setBaseCost} />
                        </ScnField>
                      }
                      adjustment={
                        <>
                          {rawCost != null ? (
                            <>
                              <Line
                                label="Base cost"
                                amount={rawCost}
                                kind="add"
                              />
                              <Line
                                label={`Contribution (≈${base > 0 ? Math.round((1 - rawCost / base) * 100) : 0}%)`}
                                amount={base - rawCost}
                                kind="add"
                              />
                            </>
                          ) : (
                            <Empty>
                              Set a unit cost to split base cost from
                              contribution.
                            </Empty>
                          )}
                          <Line
                            label="Base price"
                            amount={base}
                            kind="subtotal"
                          />
                          <Empty>
                            Placeholder. Base price isn't wired to Tharstern
                            cost yet.
                          </Empty>
                        </>
                      }
                    />
                  )}

                  {f.key === "speed" && (
                    <FactorBody
                      scenario={
                        <ScnField label="Speed">
                          <Choice
                            value={speedCode}
                            onChange={setSpeedCode}
                            options={draft.speed.ladder.map((s) => ({
                              value: s.speedCode,
                              label: s.label,
                            }))}
                            ariaLabel="Speed"
                          />
                        </ScnField>
                      }
                      adjustment={
                        speedLine ? (
                          <QuoteRow line={speedLine} />
                        ) : (
                          <Empty>
                            No uplift: this Speed is priced at its cheapest
                            option. The example reads the cost as
                            Standard&apos;s.
                          </Empty>
                        )
                      }
                    />
                  )}

                  {f.key === "commitment" && (
                    <FactorBody
                      scenario={
                        <ScnField label="Spend to date">
                          <MoneyInput value={spend} onChange={setSpend} />
                        </ScnField>
                      }
                      adjustment={
                        <>
                          {commitLine ? (
                            <QuoteRow line={commitLine} />
                          ) : (
                            <Empty>
                              No commitment discount on this price group.
                            </Empty>
                          )}
                          {rebateLine && <QuoteRow line={rebateLine} />}
                        </>
                      }
                    />
                  )}

                  {f.key === "market" && (
                    <FactorBody
                      scenario={
                        <ScnField label="Competitor">
                          <MoneyInput
                            value={competitor}
                            onChange={setCompetitor}
                          />
                        </ScnField>
                      }
                      adjustment={
                        marketLine ? (
                          <QuoteRow line={marketLine} />
                        ) : (
                          <Empty>No market adjustment.</Empty>
                        )
                      }
                    />
                  )}

                  {f.key === "capacity" && (
                    <FactorBody
                      scenario={
                        <div className="space-y-1.5">
                          <div className={microLabel}>
                            Live load — the areas this job uses
                          </div>
                          <div className="flex flex-wrap gap-x-4 gap-y-2">
                            {(item ? capacityAreasFor(item) : []).map(
                              ({ area }) => (
                                <ScnField key={area} label={area}>
                                  <PctInput
                                    value={
                                      capacityLoad[area] ??
                                      DEFAULT_CAP_LOADS[area] ??
                                      0.6
                                    }
                                    onChange={(v) =>
                                      setCapacityLoad((m) => ({
                                        ...m,
                                        [area]: v ?? 0,
                                      }))
                                    }
                                  />
                                </ScnField>
                              ),
                            )}
                          </div>
                        </div>
                      }
                      adjustment={
                        capLine ? (
                          <QuoteRow line={capLine} />
                        ) : (
                          <Empty>No capacity adjustment at these loads.</Empty>
                        )
                      }
                    />
                  )}

                  {f.key === "guardrails" && (
                    <FactorBody
                      scenario={
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                          <ScnField label="Base cost">
                            <MoneyInput
                              value={baseCost}
                              onChange={setBaseCost}
                            />
                          </ScnField>
                          <ScnField label="Prior price">
                            <MoneyInput value={prior} onChange={setPrior} />
                          </ScnField>
                        </div>
                      }
                      adjustment={
                        quote?.verdict ? (
                          <VerdictBadges verdict={quote.verdict} />
                        ) : (
                          <Empty>
                            Add a base cost or prior price to test the
                            guardrails.
                          </Empty>
                        )
                      }
                    />
                  )}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>

        {/* Final price */}
        <Separator />
        <div className="flex items-end justify-between gap-2">
          <div>
            <div className={microLabel}>Final price</div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-semibold tabular-nums">
                {quote ? formatCurrencyGbp(quote.quoted) : "—"}
              </span>
              {quote && (
                <span className="text-xs text-muted-foreground">
                  £{quote.perUnit.toFixed(2)}/unit
                </span>
              )}
              {quote &&
                draft.guardrails.rounding !== "none" &&
                quote.quoted !== runAfter.guardrails && (
                  <span className="text-xs text-muted-foreground">
                    · rounded up to {draft.guardrails.rounding} from{" "}
                    {formatCurrencyGbp(runAfter.guardrails)}
                  </span>
                )}
            </div>
          </div>
          {quote?.verdict && (
            <VerdictBadges verdict={quote.verdict} className="justify-end" />
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function FactorBody({
  scenario,
  adjustment,
}: {
  scenario: React.ReactNode;
  adjustment: React.ReactNode;
}) {
  return (
    <>
      <div className="space-y-1.5">
        <div className={microLabel}>Scenario</div>
        {scenario}
      </div>
      <Separator />
      <div className="space-y-1">
        <div className={microLabel}>Adjustment</div>
        <div className="space-y-1 text-sm">{adjustment}</div>
      </div>
    </>
  );
}

function ScnField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <Label className="font-normal text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="text-xs text-muted-foreground">{children}</div>;
}

function Line({
  label,
  amount,
  kind = "add",
}: {
  label: string;
  amount: number;
  kind?: "add" | "discount" | "subtotal";
}) {
  // An "add" line can go negative (e.g. cost above the base price wipes out the
  // contribution); show its real sign rather than a stacked "+−".
  const sign =
    kind === "discount" ? "−" : kind === "add" ? (amount < 0 ? "−" : "+") : "";
  return (
    <div
      className={cn(
        "flex items-center justify-between",
        kind === "subtotal" && "border-t pt-1 font-medium",
      )}
    >
      <span className={kind === "subtotal" ? "" : "text-muted-foreground"}>
        {label}
      </span>
      <span
        className={cn("tabular-nums", kind === "discount" && "text-green-700")}
      >
        {sign}
        {formatCurrencyGbp(Math.abs(amount))}
      </span>
    </div>
  );
}

function QuoteRow({ line }: { line: QuoteLine }) {
  const sign = line.kind === "discount" ? "−" : line.kind === "add" ? "+" : "";
  return (
    <div className="space-y-0.5">
      <div className="flex items-center justify-between font-medium">
        <span>
          {line.label}
          {line.applied === false && (
            <span className="ml-1.5 text-xs font-normal text-muted-foreground">
              suggested
            </span>
          )}
        </span>
        <span className="tabular-nums">
          {sign}
          {formatCurrencyGbp(line.amount)}
        </span>
      </div>
      {line.detail?.map((d, i) => (
        <div
          key={i}
          className="flex items-center justify-between pl-3 text-xs text-muted-foreground"
        >
          <span>{d.label}</span>
          <span className="tabular-nums">{d.value}</span>
        </div>
      ))}
    </div>
  );
}

// One badge per triggered guardrail, each carrying only its own reason; when
// within limits, a single badge with the contribution as context.
function VerdictBadges({
  verdict,
  className,
}: {
  verdict: NonNullable<PriceQuote["verdict"]>;
  className?: string;
}) {
  if (verdict.status === "ok") {
    return (
      <Badge variant="success">
        {VERDICT.ok.label}
        {verdict.marginPct != null && (
          <> · contribution {Math.round(verdict.marginPct * 100)}%</>
        )}
      </Badge>
    );
  }
  return (
    <div className={cn("flex flex-wrap items-center gap-1.5", className)}>
      {verdict.checks.map((c, i) => (
        <Badge key={i} variant={c.status === "blocked" ? "error" : "warning"}>
          {c.status === "blocked" ? "✕" : "⚠"} {c.message}
        </Badge>
      ))}
    </div>
  );
}

// ── Inputs (over the shared primitives) ──────────────────────────────────────

// Native number steppers collide with the £/% affix; hide them for a clean field.
const NO_SPIN =
  "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none";

function MoneyInput({
  value,
  onChange,
  onBlur,
  step,
  className,
}: {
  value: number | undefined;
  onChange: (v: number | undefined) => void;
  onBlur?: () => void;
  step?: string;
  className?: string;
}) {
  return (
    <div className={cn("relative", className ?? "w-28")}>
      <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
        £
      </span>
      <Input
        type="number"
        step={step}
        value={value ?? ""}
        onFocus={(e) => e.currentTarget.select()}
        onBlur={onBlur}
        onChange={(e) =>
          onChange(e.target.value === "" ? undefined : Number(e.target.value))
        }
        className={cn("pl-6 text-right tabular-nums", NO_SPIN)}
      />
    </div>
  );
}

function PctInput({
  id,
  value,
  onChange,
  onBlur,
  allowNull,
  min,
  placeholder,
  className,
}: {
  id?: string;
  value: number | null;
  onChange: (v: number | null) => void;
  onBlur?: () => void;
  allowNull?: boolean;
  /** Displayed-percent floor (e.g. 0), for fields with no negative sense. */
  min?: number;
  /** Shown when the field is blank (allowNull), e.g. the value blank stands for. */
  placeholder?: string;
  className?: string;
}) {
  return (
    <div className={cn("relative", className ?? "w-20")}>
      <Input
        id={id}
        type="number"
        min={min}
        placeholder={placeholder}
        value={value == null ? "" : displayedPct(value)}
        onFocus={(e) => e.currentTarget.select()}
        onBlur={onBlur}
        onChange={(e) => {
          if (e.target.value === "") {
            onChange(allowNull ? null : 0);
            return;
          }
          const typedPct = Number(e.target.value);
          if (!Number.isFinite(typedPct)) return;
          // `min` is a floor, not just a browser hint: this page saves from a
          // click handler, not a form submit, so constraint validation never
          // runs and a typed "-5" would otherwise reach the API and come back
          // as an unbound 422 the operator cannot attribute to a field.
          const flooredPct = min != null ? Math.max(typedPct, min) : typedPct;
          onChange(flooredPct / 100);
        }}
        className={cn("pr-6 text-right tabular-nums", NO_SPIN)}
      />
      <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
        %
      </span>
    </div>
  );
}

/**
 * A stored fraction as the percent to show. Not rounded to whole percent: the
 * backend applies 7.5% exactly, so displaying "8" would make this screen
 * misstate the live policy. Trimmed to 4 decimal places only to keep float
 * noise (0.075 * 100 === 7.500000000000001) off the screen.
 */
function displayedPct(fraction: number): number {
  return Number((fraction * 100).toFixed(4));
}

// A bare-magnitude percentage field (string-valued, % suffix) for guardrail
// thresholds — no sign, no units in the cell, just the number. Selects on focus
// so overtyping replaces rather than prepends, and strips any leading zero.
function PctTextCell({
  value,
  onChange,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  className?: string;
}) {
  return (
    <div className={cn("relative", className ?? "w-20")}>
      <Input
        inputMode="decimal"
        value={value}
        onFocus={(e) => e.currentTarget.select()}
        onChange={(e) => onChange(e.target.value.replace(/^0+(?=\d)/, ""))}
        className={cn("pr-6 text-right tabular-nums", NO_SPIN)}
      />
      <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
        %
      </span>
    </div>
  );
}

function Choice({
  value,
  onChange,
  options,
  className,
  ariaLabel,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  className?: string;
  ariaLabel?: string;
  placeholder?: string;
}) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger
        size="sm"
        aria-label={ariaLabel}
        className={cn("w-auto min-w-[8rem]", className)}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ── Panels (editable) ────────────────────────────────────────────────────────

function PanelShell({
  title,
  hint,
  action,
  children,
}: {
  title: string;
  hint?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        {hint && <CardDescription>{hint}</CardDescription>}
        {action && <CardAction>{action}</CardAction>}
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  );
}

function EnableSwitch({
  checked,
  onCheckedChange,
}: {
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
}) {
  return (
    <Label className="gap-2 text-xs font-normal text-muted-foreground">
      {checked ? "On" : "Off"}
      <Switch checked={checked} onCheckedChange={onCheckedChange} />
    </Label>
  );
}

function CommercialPanel({
  draft,
  onEdit,
}: {
  draft: PriceGroup;
  onEdit: (d: PriceGroup) => void;
}) {
  const c = draft.commercial;
  const pct = Math.round(c.contributionMarginPct * 100);
  const exCost = 100;
  const exPrice =
    c.contributionMarginPct < 1
      ? Math.round(exCost / (1 - c.contributionMarginPct))
      : exCost;
  return (
    <PanelShell
      title="Base"
      hint="The base price is determined from Tharstern's job cost and a target contribution. Set the contribution below."
    >
      <div className="flex items-center justify-between gap-3 max-w-sm">
        <Label className="font-normal text-muted-foreground">
          Target contribution
        </Label>
        <PctInput
          value={c.contributionMarginPct}
          onChange={(v) =>
            onEdit({
              ...draft,
              commercial: { ...c, contributionMarginPct: v ?? 0 },
            })
          }
        />
      </div>
      <div className="space-y-1 max-w-sm">
        <div className="flex items-center justify-between gap-3">
          <Label
            htmlFor="bought-in-margin"
            className="font-normal text-muted-foreground"
          >
            Target margin, bought-in
          </Label>
          <PctInput
            id="bought-in-margin"
            value={c.boughtInContributionMarginPct}
            allowNull
            min={0}
            placeholder={String(pct)}
            onChange={(v) =>
              onEdit({
                ...draft,
                commercial: { ...c, boughtInContributionMarginPct: v },
              })
            }
          />
        </div>
        <p className="text-xs text-muted-foreground">
          For supplier quotes, which include delivery and no speed premium.
          Required to save a version; until one is set, supplier quotes use the
          in-house target.
        </p>
      </div>
      <div className="space-y-3 rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
        <div className="font-medium text-foreground">How it works</div>
        <div className="space-y-0.5">
          <div className={microLabel}>Formula</div>
          <div className="text-foreground">
            base price = base cost ÷ (1 − target contribution)
          </div>
        </div>
        <div className="space-y-0.5">
          <div className={microLabel}>Example</div>
          <div className="tabular-nums text-foreground">
            £{exCost} ÷ (1 − {pct}%) ={" "}
            <span className="font-medium">£{exPrice} base price</span>
            <span className="text-muted-foreground">
              {" "}
              (£{exPrice - exCost} / {pct}% contribution)
            </span>
          </div>
        </div>
      </div>
    </PanelShell>
  );
}

/**
 * The Speed price rule (d65, mock-up state 8): for each Speed faster than
 * Standard, a percentage over Standard with an optional £ minimum and maximum.
 * Each Speed is priced at its cheapest option or Standard plus the uplift,
 * whichever is higher, and never below a slower one. The days are Time's
 * ladder, read-only here; Economy (slower) is priced at its cheapest option.
 */
function SpeedPriceRulePanel({
  draft,
  onEdit,
}: {
  draft: PriceGroup;
  onEdit: (d: PriceGroup) => void;
}) {
  const { rule, ladder, baselineCode } = draft.speed;
  const baselineAt = ladder.findIndex((s) => s.speedCode === baselineCode);
  const entryFor = (code: string) =>
    rule.entries.find((e) => e.speedCode === code);
  const setEntry = (
    speedCode: string,
    change: Partial<Omit<SpeedUplift, "speedCode">>,
  ) => {
    const current = entryFor(speedCode) ?? {
      speedCode,
      upliftPct: 0,
      minimumUpliftPence: null,
      maximumUpliftPence: null,
    };
    const next = { ...current, ...change };
    const entries = rule.entries.some((e) => e.speedCode === speedCode)
      ? rule.entries.map((e) => (e.speedCode === speedCode ? next : e))
      : [...rule.entries, next];
    onEdit({ ...draft, speed: { ...draft.speed, rule: { entries } } });
  };
  const toPence = (pounds: number | undefined) =>
    pounds == null ? null : Math.round(pounds * 100);
  return (
    <PanelShell
      title="Speed price rule"
      hint="Each Speed's price is the cheapest way to get it there, or the Standard price plus the uplift below, whichever is higher. A Speed is never priced below a slower one."
    >
      <Table className="[&_tbody_tr:hover]:bg-transparent">
        <TableHeader>
          <TableRow>
            <TableHead>Speed</TableHead>
            <TableHead>Arrives within</TableHead>
            <TableHead className="text-right">Uplift over Standard</TableHead>
            <TableHead className="text-right">At least</TableHead>
            <TableHead className="text-right">At most</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {ladder.map((speed, i) => {
            const entry = entryFor(speed.speedCode);
            const isFaster = i > baselineAt;
            return (
              <TableRow key={speed.speedCode}>
                <TableCell className="font-medium">
                  <span className="flex flex-wrap items-center gap-1.5">
                    {speed.label}
                    {speed.needsAPerson && (
                      <Badge variant="outline">Needs a person</Badge>
                    )}
                  </span>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {speed.workingDaysToArrival} working day
                  {speed.workingDaysToArrival === 1 ? "" : "s"}
                </TableCell>
                {isFaster ? (
                  <>
                    <TableCell>
                      <span className="flex justify-end">
                        <PctInput
                          className="w-20"
                          value={entry?.upliftPct ?? 0}
                          min={0}
                          onChange={(v) =>
                            setEntry(speed.speedCode, { upliftPct: v ?? 0 })
                          }
                        />
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="flex justify-end">
                        <MoneyInput
                          value={
                            entry?.minimumUpliftPence == null
                              ? undefined
                              : entry.minimumUpliftPence / 100
                          }
                          onChange={(v) =>
                            setEntry(speed.speedCode, {
                              minimumUpliftPence: toPence(v),
                            })
                          }
                          step="0.01"
                        />
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="flex justify-end">
                        <MoneyInput
                          value={
                            entry?.maximumUpliftPence == null
                              ? undefined
                              : entry.maximumUpliftPence / 100
                          }
                          onChange={(v) =>
                            setEntry(speed.speedCode, {
                              maximumUpliftPence: toPence(v),
                            })
                          }
                          step="0.01"
                        />
                      </span>
                    </TableCell>
                  </>
                ) : (
                  <TableCell
                    colSpan={3}
                    className="text-right text-muted-foreground"
                  >
                    {i === baselineAt
                      ? "The baseline"
                      : "Priced at its cheapest option"}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </PanelShell>
  );
}

function CommitmentPanel({
  draft,
  catalogueItems,
  onEdit,
}: {
  draft: PriceGroup;
  catalogueItems: CatalogueItem[];
  onEdit: (d: PriceGroup) => void;
}) {
  const c = draft.commitment;
  const setEnabled = (on: boolean) =>
    onEdit({
      ...draft,
      commitment: on
        ? (c ?? {
            discountDefaultPct: 0.05,
            discountExceptions: [],
            rebate: null,
          })
        : null,
    });
  return (
    <PanelShell
      title="Commitment"
      hint="The savings we can offer a customer based on the commitment they give us. Either as a discount on every order, or as a rebate on total spend over a full year. Set a discount or rebate below."
      action={<EnableSwitch checked={c != null} onCheckedChange={setEnabled} />}
    >
      {c == null ? (
        <div className="text-sm text-muted-foreground">
          Off. No account-level discount or rebate on this price group.
        </div>
      ) : (
        <CommitmentEditor
          catalogueItems={catalogueItems}
          commitment={c}
          onChange={(next) => onEdit({ ...draft, commitment: next })}
        />
      )}
    </PanelShell>
  );
}

function CommitmentEditor({
  catalogueItems,
  commitment: c,
  onChange: setC,
}: {
  catalogueItems: CatalogueItem[];
  commitment: CommitmentConfig;
  onChange: (c: CommitmentConfig) => void;
}) {
  const items = catalogueItems;
  const productTypes = [...new Set(items.map((it) => it.productLabel))];
  const usedTypes = new Set(c.discountExceptions.map((e) => e.productType));
  const addable = productTypes.filter((t) => !usedTypes.has(t));
  const setEx = (
    i: number,
    patch: Partial<(typeof c.discountExceptions)[number]>,
  ) =>
    setC({
      ...c,
      discountExceptions: c.discountExceptions.map((x, j) =>
        j === i ? { ...x, ...patch } : x,
      ),
    });
  const rebate = c.rebate;
  const setRebate = (next: typeof c.rebate) => setC({ ...c, rebate: next });
  const setBand = (i: number, patch: Partial<RebateBand>) =>
    rebate &&
    setRebate({
      ...rebate,
      bands: rebate.bands.map((x, j) => (j === i ? { ...x, ...patch } : x)),
    });
  const removeBand = (i: number) =>
    rebate &&
    setRebate({ ...rebate, bands: rebate.bands.filter((_, j) => j !== i) });
  const addBand = () => {
    if (!rebate) return;
    const topFinite = rebate.bands.reduce(
      (m, b) => (b.upTo != null ? Math.max(m, b.upTo) : m),
      0,
    );
    // Insert before the open ("and above") band so stored order stays sorted.
    const openIdx = rebate.bands.findIndex((b) => b.upTo == null);
    const next = [...rebate.bands];
    next.splice(openIdx < 0 ? next.length : openIdx, 0, {
      upTo: topFinite + 250000,
      pct: 0.03,
    });
    setRebate({ ...rebate, bands: next });
  };
  // Re-tidy into bracket order on commit (blur), so editing never reorders rows
  // mid-keystroke — that was making the cursor jump between fields.
  const tidyBands = () =>
    rebate && setRebate({ ...rebate, bands: orderRebateBands(rebate.bands) });
  // Render in stored order; each bracket's lower bound is £0 or the previous
  // bracket's upper + £1.
  let lower = 0;
  const rebateRows = (rebate?.bands ?? []).map((b, idx) => {
    const from = lower;
    if (b.upTo != null) lower = b.upTo;
    return { ...b, idx, from };
  });

  return (
    <>
      {/* Discount — default + per-product-type exceptions (Market mechanic). */}
      <div className="space-y-2">
        <div className="text-sm font-medium">Discount</div>
        <Table className="[&_tbody_tr:hover]:bg-transparent">
          <TableHeader>
            <TableRow>
              <TableHead>Applies to</TableHead>
              <TableHead>Discount</TableHead>
              <TableHead className="w-8" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {c.discountExceptions.map((ex, i) => (
              <TableRow key={ex.productType}>
                <TableCell>{ex.productType}</TableCell>
                <TableCell>
                  <PctInput
                    value={ex.pct}
                    onChange={(v) => setEx(i, { pct: v ?? 0 })}
                  />
                </TableCell>
                <TableCell>
                  <RowDelete
                    onClick={() =>
                      setC({
                        ...c,
                        discountExceptions: c.discountExceptions.filter(
                          (_, j) => j !== i,
                        ),
                      })
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell className="font-medium text-muted-foreground">
                {c.discountExceptions.length ? "Everything else" : "Everything"}
              </TableCell>
              <TableCell>
                <PctInput
                  value={c.discountDefaultPct}
                  onChange={(v) => setC({ ...c, discountDefaultPct: v ?? 0 })}
                />
              </TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
        {addable.length > 0 && (
          <Choice
            value=""
            onChange={(t) =>
              setC({
                ...c,
                discountExceptions: [
                  ...c.discountExceptions,
                  { productType: t, pct: c.discountDefaultPct },
                ],
              })
            }
            options={addable.map((t) => ({ value: t, label: t }))}
            placeholder="＋ add product type"
            ariaLabel="Add product type"
          />
        )}
      </div>

      {/* Rebate — incremental bands on cumulative annual spend. */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium">Rebate on annual spend</div>
          <EnableSwitch
            checked={rebate != null}
            onCheckedChange={(on) =>
              setRebate(
                on
                  ? {
                      period: "Contract year",
                      bands: [
                        { upTo: 500000, pct: 0 },
                        { upTo: null, pct: 0.03 },
                      ],
                    }
                  : null,
              )
            }
          />
        </div>
        {rebate == null ? (
          <div className="text-xs text-muted-foreground">
            Off. No year-end rebate on cumulative spend.
          </div>
        ) : (
          <>
            <Table className="[&_tbody_tr:hover]:bg-transparent">
              <TableHeader>
                <TableRow>
                  <TableHead>Annual spend from</TableHead>
                  <TableHead>Up to</TableHead>
                  <TableHead>Rebate</TableHead>
                  <TableHead className="w-8" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rebateRows.map((row) => (
                  <TableRow key={row.idx}>
                    <TableCell className="tabular-nums text-muted-foreground">
                      {row.from === 0
                        ? "£0"
                        : `£${(row.from + 1).toLocaleString()}`}
                    </TableCell>
                    <TableCell>
                      {row.upTo == null ? (
                        <span className="text-muted-foreground">and above</span>
                      ) : (
                        <MoneyInput
                          value={row.upTo}
                          onChange={(v) => setBand(row.idx, { upTo: v ?? 0 })}
                          onBlur={tidyBands}
                          className="w-32"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <PctInput
                        value={row.pct}
                        onChange={(v) => setBand(row.idx, { pct: v ?? 0 })}
                      />
                    </TableCell>
                    <TableCell>
                      <RowDelete onClick={() => removeBand(row.idx)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <AddButton label="band" onClick={addBand} />
            <RebateExample bands={rebate.bands} />
          </>
        )}
      </div>
    </>
  );
}

// Live worked example for the rebate: spells out the incremental (tax-band)
// computation against a sample spend just above the top band.
function RebateExample({ bands }: { bands: RebateBand[] }) {
  if (!bands.length) return null;
  const topFinite = bands.reduce(
    (m, b) => (b.upTo != null ? Math.max(m, b.upTo) : m),
    0,
  );
  const exampleSpend =
    Math.ceil(Math.max(topFinite * 1.2, topFinite + 150000) / 50000) * 50000;
  const { slices, earned, effectivePct } = rebateBreakdown(bands, exampleSpend);
  const k = (n: number) => `£${n.toLocaleString()}`;
  const start = (from: number) => (from === 0 ? k(0) : k(from + 1));
  return (
    <div className="space-y-3 rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
      <div className="font-medium text-foreground">How it works</div>
      <div>
        Each band's rate applies only to the spend that falls within it, like
        tax bands. The slices then sum into the rebate the customer has earned.
      </div>
      <div className="space-y-0.5">
        <div className={microLabel}>
          Example at {k(exampleSpend)} annual spend
        </div>
        {slices.map((s, i) => (
          <div key={i} className="tabular-nums text-foreground">
            {start(s.from)} – {k(s.to)} @ {Math.round(s.pct * 100)}% ={" "}
            {k(Math.round(s.amount))}
          </div>
        ))}
        <div className="tabular-nums font-medium text-foreground">
          Total = {k(earned)} ({(effectivePct * 100).toFixed(1)}% effective)
        </div>
      </div>
    </div>
  );
}

function StanceCell({
  setting,
  onChange,
}: {
  setting: StanceSetting;
  onChange: (s: StanceSetting) => void;
}) {
  return (
    <Choice
      value={setting.stance}
      onChange={(v) => onChange({ ...setting, stance: v as Stance })}
      options={(Object.keys(STANCE_LABEL) as Stance[]).map((s) => ({
        value: s,
        label: STANCE_LABEL[s],
      }))}
      ariaLabel="Stance"
    />
  );
}

// The resulting price rule, with the tuner inline — the formula IS the control.
function EffectCell({
  setting,
  onChange,
}: {
  setting: StanceSetting;
  onChange: (s: StanceSetting) => void;
}) {
  switch (setting.stance) {
    case "hold":
      return <span className="text-muted-foreground">no adjustment</span>;
    case "track":
      return <span className="text-muted-foreground">match market</span>;
    case "win":
    case "premium":
      return (
        <span className="inline-flex items-center gap-1.5 text-muted-foreground">
          <Badge variant={setting.stance === "win" ? "success" : "secondary"}>
            {setting.stance === "win" ? "Below market" : "Above market"}
          </Badge>
          <span>by</span>
          <PctInput
            value={setting.dial ?? (setting.stance === "win" ? 0.06 : 0.05)}
            onChange={(v) => onChange({ ...setting, dial: v ?? 0 })}
          />
        </span>
      );
    case "optimise":
      return (
        <Choice
          value={String(setting.lean ?? 0)}
          onChange={(v) => onChange({ ...setting, lean: Number(v) })}
          options={[
            { value: "-0.5", label: "Favour margin (70/30)" },
            { value: "0", label: "Balanced (50/50)" },
            { value: "0.5", label: "Favour share (30/70)" },
          ]}
          ariaLabel="Lean"
          className="min-w-[10rem]"
        />
      );
  }
}

function MarketPanel({
  draft,
  catalogueItems,
  onEdit,
}: {
  draft: PriceGroup;
  catalogueItems: CatalogueItem[];
  onEdit: (d: PriceGroup) => void;
}) {
  const m = draft.market;
  const setM = (next: typeof m) => onEdit({ ...draft, market: next });
  const items = catalogueItems;
  const usedIds = new Set(m.exceptions.map((e) => e.itemId));
  const addable = items.filter((it) => !usedIds.has(it.id));
  const setException = (i: number, s: StanceSetting) =>
    setM({
      ...m,
      exceptions: m.exceptions.map((x, j) => (j === i ? { ...x, ...s } : x)),
    });
  const usesMaximise =
    m.defaultStance.stance === "optimise" ||
    m.exceptions.some((e) => e.stance === "optimise");
  return (
    <PanelShell
      title="Market"
      hint="How we price against the market. Set a default stance, then add per-item exceptions."
      action={
        <EnableSwitch
          checked={m.enabled}
          onCheckedChange={(v) => setM({ ...m, enabled: v })}
        />
      }
    >
      {/* Two gating decisions, up top. */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">
            When the AI recommends a price,
          </span>
          <Choice
            value={m.applyMode}
            onChange={(v) => setM({ ...m, applyMode: v as typeof m.applyMode })}
            options={[
              { value: "suggest", label: "a rep approves it" },
              { value: "apply", label: "apply it automatically" },
            ]}
            ariaLabel="Apply mode"
            className="min-w-[11rem]"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">
            Only apply when competitor-data confidence is at least
          </span>
          <Choice
            value={m.minConfidence}
            onChange={(v) =>
              setM({ ...m, minConfidence: v as typeof m.minConfidence })
            }
            options={[
              { value: "low", label: "Low" },
              { value: "med", label: "Medium" },
              { value: "high", label: "High" },
            ]}
            ariaLabel="Minimum confidence"
          />
        </div>
      </div>

      {/* Default + per-item exceptions as one table. */}
      <Table className="[&_tbody_tr:hover]:bg-transparent">
        <TableHeader>
          <TableRow>
            <TableHead>Applies to</TableHead>
            <TableHead>Stance</TableHead>
            <TableHead>Effect</TableHead>
            <TableHead className="w-8" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {m.exceptions.map((ex, i) => (
            <TableRow key={ex.itemId}>
              <TableCell>{ex.itemLabel}</TableCell>
              <TableCell>
                <StanceCell setting={ex} onChange={(s) => setException(i, s)} />
              </TableCell>
              <TableCell>
                <EffectCell setting={ex} onChange={(s) => setException(i, s)} />
              </TableCell>
              <TableCell>
                <RowDelete
                  onClick={() =>
                    setM({
                      ...m,
                      exceptions: m.exceptions.filter((_, j) => j !== i),
                    })
                  }
                />
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell className="font-medium text-muted-foreground">
              {m.exceptions.length ? "Everything else" : "Everything"}
            </TableCell>
            <TableCell>
              <StanceCell
                setting={m.defaultStance}
                onChange={(s) => setM({ ...m, defaultStance: s })}
              />
            </TableCell>
            <TableCell>
              <EffectCell
                setting={m.defaultStance}
                onChange={(s) => setM({ ...m, defaultStance: s })}
              />
            </TableCell>
            <TableCell />
          </TableRow>
        </TableBody>
      </Table>
      {addable.length > 0 && (
        <Choice
          value=""
          onChange={(id) => {
            const it = items.find((x) => x.id === id);
            if (it)
              setM({
                ...m,
                exceptions: [
                  ...m.exceptions,
                  { itemId: it.id, itemLabel: it.name, stance: "hold" },
                ],
              });
          }}
          options={addable.map((it) => ({ value: it.id, label: it.name }))}
          placeholder="＋ add item exception"
          ariaLabel="Add item exception"
        />
      )}

      {usesMaximise && (
        <div className="space-y-2 rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
          <div>
            <span className="font-medium text-foreground">Maximise</span> scores
            candidate prices by expected value: margin × the chance of winning
            (example at cost £300):
          </div>
          <Table className="[&_tbody_tr:hover]:bg-transparent">
            <TableHeader>
              <TableRow>
                <TableHead>Price</TableHead>
                <TableHead className="text-right">Margin</TableHead>
                <TableHead className="text-right">Win %</TableHead>
                <TableHead className="text-right">Expected</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[
                { p: 390, m: 90, w: 78, ev: 70 },
                { p: 408, m: 108, w: 71, ev: 77 },
                { p: 420, m: 120, w: 64, ev: 77, peak: true },
                { p: 450, m: 150, w: 49, ev: 74 },
              ].map((r) => (
                <TableRow
                  key={r.p}
                  className={r.peak ? "font-medium text-foreground" : ""}
                >
                  <TableCell className="py-1">
                    £{r.p}
                    {r.peak ? " ← peak" : ""}
                  </TableCell>
                  <TableCell className="py-1 text-right tabular-nums">
                    £{r.m}
                  </TableCell>
                  <TableCell className="py-1 text-right tabular-nums">
                    {r.w}%
                  </TableCell>
                  <TableCell className="py-1 text-right tabular-nums">
                    £{r.ev}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div>
            <span className="font-medium">Balanced</span> takes the peak;{" "}
            <span className="font-medium">Favour margin</span> lands higher,{" "}
            <span className="font-medium">Favour share</span> lands lower. Falls
            back to Track without a cost + win-rate.
          </div>
        </div>
      )}
    </PanelShell>
  );
}

const CAP_KIND_OPTIONS = [
  { value: "none", label: "None" },
  { value: "discount", label: "Discount" },
  { value: "premium", label: "Premium" },
];

// One area's load bands. Bands tile 0–100%; the last is fixed at 100%. Editing
// never reorders rows mid-keystroke (stored order); tidy on blur. Removing a
// trigger merges upward, keeping the lower band's adjustment.
function CapacityBandsEditor({
  label,
  bands,
  onChange,
  onRemove,
}: {
  label: string;
  bands: CapacityBand[];
  onChange: (b: CapacityBand[]) => void;
  onRemove?: () => void;
}) {
  const setBand = (i: number, patch: Partial<CapacityBand>) =>
    onChange(bands.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const tidy = () => onChange([...bands].sort((a, b) => a.upTo - b.upTo));
  const addTrigger = () => {
    const term = Math.max(bands.length - 1, 0);
    const prev = term > 0 ? bands[term - 1].upTo : 0;
    const upTo = Math.min(0.95, Math.round(((prev + 1) / 2) * 20) / 20);
    const next = [...bands];
    next.splice(term, 0, { upTo, kind: "none", pct: 0 });
    onChange(next);
  };
  const removeTrigger = (i: number) => {
    const next = [...bands];
    if (i < next.length - 1)
      next[i + 1] = { ...next[i + 1], kind: next[i].kind, pct: next[i].pct };
    next.splice(i, 1);
    onChange(next);
  };
  let lower = 0;
  const rows = bands.map((b, idx) => {
    const from = lower;
    lower = b.upTo;
    return { ...b, idx, from };
  });
  const lastIdx = rows.length - 1;
  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium">{label}</div>
        {onRemove && <RowDelete onClick={onRemove} />}
      </div>
      <Table className="[&_tbody_tr:hover]:bg-transparent">
        <TableHeader>
          <TableRow>
            <TableHead>Load from</TableHead>
            <TableHead>Up to</TableHead>
            <TableHead>Adjustment</TableHead>
            <TableHead className="w-8" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.idx}>
              <TableCell className="tabular-nums text-muted-foreground">
                {row.from === 0 ? "0%" : `${Math.round(row.from * 100) + 1}%`}
              </TableCell>
              <TableCell>
                {row.idx === lastIdx ? (
                  <span className="text-muted-foreground">100%</span>
                ) : (
                  <PctInput
                    value={row.upTo}
                    onChange={(v) =>
                      setBand(row.idx, { upTo: Math.min(0.99, v ?? 0) })
                    }
                    onBlur={tidy}
                  />
                )}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Choice
                    value={row.kind}
                    onChange={(v) =>
                      setBand(row.idx, {
                        kind: v as CapacityBand["kind"],
                        pct: v === "none" ? 0 : row.pct || 0.1,
                      })
                    }
                    options={CAP_KIND_OPTIONS}
                    ariaLabel="Adjustment kind"
                    className="min-w-[7rem]"
                  />
                  {row.kind !== "none" && (
                    <PctInput
                      value={row.pct}
                      onChange={(v) => setBand(row.idx, { pct: v ?? 0 })}
                    />
                  )}
                </div>
              </TableCell>
              <TableCell>
                {row.idx !== lastIdx && (
                  <RowDelete onClick={() => removeTrigger(row.idx)} />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <AddButton label="trigger point" onClick={addTrigger} />
    </div>
  );
}

function CapacityPanel({
  draft,
  onEdit,
}: {
  draft: PriceGroup;
  onEdit: (d: PriceGroup) => void;
}) {
  const c = draft.capacity;
  const setC = (next: typeof c) => onEdit({ ...draft, capacity: next });
  const usedAreas = new Set(c.overrides.map((o) => o.area));
  const addableAreas = CAPACITY_AREAS.filter((a) => !usedAreas.has(a));
  return (
    <PanelShell
      title="Capacity"
      hint="How the price responds to production-area workload. Each band's adjustment applies to that area's share of the job cost (not the whole price); the areas a job touches then sum. Set a default for every area, then call out specific areas."
      action={
        <EnableSwitch
          checked={c.enabled}
          onCheckedChange={(v) => setC({ ...c, enabled: v })}
        />
      }
    >
      {c.overrides.map((ov, i) => (
        <CapacityBandsEditor
          key={ov.area}
          label={ov.area}
          bands={ov.bands}
          onChange={(bands) =>
            setC({
              ...c,
              overrides: c.overrides.map((x, j) =>
                j === i ? { ...x, bands } : x,
              ),
            })
          }
          onRemove={() =>
            setC({ ...c, overrides: c.overrides.filter((_, j) => j !== i) })
          }
        />
      ))}
      <CapacityBandsEditor
        label={c.overrides.length ? "Everything else" : "Everything"}
        bands={c.defaultBands}
        onChange={(bands) => setC({ ...c, defaultBands: bands })}
      />
      {addableAreas.length > 0 && (
        <Choice
          value=""
          onChange={(area) =>
            setC({
              ...c,
              overrides: [
                ...c.overrides,
                { area, bands: c.defaultBands.map((b) => ({ ...b })) },
              ],
            })
          }
          options={addableAreas.map((a) => ({ value: a, label: a }))}
          placeholder="＋ add area"
          ariaLabel="Add area"
        />
      )}
    </PanelShell>
  );
}

function GuardrailsPanel({
  draft,
  onEdit,
}: {
  draft: PriceGroup;
  onEdit: (d: PriceGroup) => void;
}) {
  const g = draft.guardrails;
  const setG = (next: typeof g) => onEdit({ ...draft, guardrails: next });
  const repeatRow = g.rows.find((r) => r.directional) ?? null;
  return (
    <PanelShell
      title="Guardrails"
      hint="The overall guardrails on pricing, after all other calculations are complete. Set the approval and hard thresholds below."
    >
      <Table className="[&_tbody_tr:hover]:bg-transparent">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[48%]" />
            <TableHead className="w-40">Needs approval</TableHead>
            <TableHead className="w-40">Hard limit</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {g.rows.map((r, i) => {
            const patch = (p: Partial<typeof r>) =>
              setG({
                ...g,
                rows: g.rows.map((x, j) => (j === i ? { ...x, ...p } : x)),
              });
            return (
              <TableRow key={i} className="align-top">
                <TableCell>
                  <div className="font-medium">{r.name}</div>
                  <div className="max-w-[20rem] whitespace-normal text-xs text-muted-foreground">
                    {r.description}
                  </div>
                </TableCell>
                <TableCell>
                  {r.directional ? (
                    <DirectionalLimit
                      up={r.approval}
                      down={r.approvalDown ?? r.approval}
                      onUp={(v) => patch({ approval: v })}
                      onDown={(v) => patch({ approvalDown: v })}
                    />
                  ) : (
                    <PctTextCell
                      value={r.approval}
                      onChange={(v) => patch({ approval: v })}
                    />
                  )}
                </TableCell>
                <TableCell>
                  {r.directional ? (
                    <DirectionalLimit
                      up={r.hard}
                      down={r.hardDown ?? r.hard}
                      onUp={(v) => patch({ hard: v })}
                      onDown={(v) => patch({ hardDown: v })}
                    />
                  ) : (
                    <PctTextCell
                      value={r.hard}
                      onChange={(v) => patch({ hard: v })}
                    />
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {repeatRow && (
        <RepeatPriceCalibrationReadout
          priceGroupId={draft.id}
          row={repeatRow}
        />
      )}
      <div className="flex items-center gap-3">
        <Label className="font-normal text-muted-foreground">Rounding</Label>
        <Choice
          value={g.rounding}
          onChange={(v) => setG({ ...g, rounding: v })}
          options={[
            { value: "50p", label: "round up to 50p" },
            { value: "£1", label: "round up to £1" },
            { value: "£5", label: "round up to £5" },
            { value: "£10", label: "round up to £10" },
            { value: "none", label: "No rounding" },
          ]}
          ariaLabel="Rounding"
        />
      </div>
    </PanelShell>
  );
}

/**
 * The pricing hierarchy's first rung (price-precedent brief, decision
 * 2026-09-15): whether a customer's last ordered price is suggested ahead of
 * the policy price, how far back and how close in quantity a prior order may
 * be, and which margin floor it must still clear at today's cost.
 */
function PrecedentPanel({
  draft,
  onEdit,
}: {
  draft: PriceGroup;
  onEdit: (d: PriceGroup) => void;
}) {
  const p: PrecedentConfig = draft.precedent ?? {
    suggestLastOrdered: true,
    windowMonths: 6,
    quantityTolerancePct: 10,
    floor: "both",
  };
  const set = (next: Partial<PrecedentConfig>) =>
    onEdit({ ...draft, precedent: { ...p, ...next } });
  return (
    <PanelShell
      title="Precedent"
      hint="What the customer last paid for the same spec is the suggested price, ahead of the policy price, when it still clears the margin floor for that type of work at today's cost. The policy price is the level a CSR may drop to; the repeat-price guardrail governs the drop."
    >
      <div className="flex items-center justify-between gap-4">
        <div>
          <Label>Suggest the last ordered price</Label>
          <p className="text-xs text-muted-foreground">
            Off: the policy price is always suggested; the last ordered price
            still shows as a level.
          </p>
        </div>
        <Switch
          checked={p.suggestLastOrdered}
          onCheckedChange={(v) => set({ suggestLastOrdered: v })}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="precedent-window">Look back</Label>
          <div className="flex items-center gap-2">
            <Input
              id="precedent-window"
              type="number"
              min={1}
              className="w-20"
              value={p.windowMonths}
              onChange={(e) =>
                set({
                  windowMonths: Math.max(1, parseInt(e.target.value, 10) || 1),
                })
              }
            />
            <span className="text-sm text-muted-foreground">months</span>
          </div>
        </div>
        <div className="space-y-1">
          <Label htmlFor="precedent-qty">Quantity tolerance</Label>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">±</span>
            <Input
              id="precedent-qty"
              type="number"
              min={0}
              max={100}
              className="w-20"
              value={p.quantityTolerancePct}
              onChange={(e) =>
                set({
                  quantityTolerancePct: Math.max(
                    0,
                    parseInt(e.target.value, 10) || 0,
                  ),
                })
              }
            />
            <span className="text-sm text-muted-foreground">
              % of the quoted quantity
            </span>
          </div>
        </div>
      </div>
      <div className="space-y-1">
        <Label>Floor the last ordered price must clear at today's cost</Label>
        <Choice
          value={p.floor}
          onChange={(v) => set({ floor: v as PrecedentConfig["floor"] })}
          options={[
            {
              value: "both",
              label: "Work-style floor and this group's contribution guardrail",
            },
            {
              value: "work_style",
              label: "Work-style floor only (Pricing targets)",
            },
            {
              value: "price_group",
              label: "This group's contribution guardrail only",
            },
          ]}
          ariaLabel="Precedent floor"
        />
        <p className="text-xs text-muted-foreground">
          Work-style floors are the review targets on the Pricing targets page.
          A last ordered price below the chosen floor is shown greyed with the
          reason, and policy is suggested instead.
        </p>
      </div>
    </PanelShell>
  );
}

/**
 * What the repeat-price thresholds being typed would have done to the last
 * six months' repeats (price-precedent brief J4): the flag volume is known
 * before the save, not discovered on the board afterwards. Re-reads on every
 * edit, debounced, against the same lookup the guardrail itself will use.
 */
function RepeatPriceCalibrationReadout({
  priceGroupId,
  row,
}: {
  priceGroupId: string;
  row: GuardrailRow;
}) {
  const adapter = useDataAdapter();
  const thresholds = useMemo<RepeatPriceThresholds | null>(() => {
    const n = (v: string | undefined) =>
      v == null || v === "" ? NaN : Number(v);
    const t = {
      up_approval: n(row.approval),
      down_approval: n(row.approvalDown ?? row.approval),
      up_hard: n(row.hard),
      down_hard: n(row.hardDown ?? row.hard),
    };
    return Object.values(t).every((x) => Number.isFinite(x)) ? t : null;
  }, [row.approval, row.approvalDown, row.hard, row.hardDown]);
  const [state, setState] = useState<
    | { kind: "idle" }
    | { kind: "loading" }
    | { kind: "ready"; calibration: RepeatPriceCalibration }
    | { kind: "error" }
  >({ kind: "idle" });
  useEffect(() => {
    if (!thresholds) return;
    let cancelled = false;
    setState((s) => (s.kind === "ready" ? s : { kind: "loading" }));
    const handle = setTimeout(() => {
      adapter
        .getRepeatPriceCalibration(priceGroupId, thresholds)
        .then((calibration) => {
          if (!cancelled) setState({ kind: "ready", calibration });
        })
        .catch(() => {
          if (!cancelled) setState({ kind: "error" });
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [adapter, priceGroupId, thresholds]);

  return (
    <div
      className="rounded-md border border-dashed border-border px-3 py-2 text-sm"
      aria-live="polite"
    >
      {!thresholds ? (
        <span className="text-muted-foreground">
          Enter all four repeat-price limits to see what they would have
          flagged.
        </span>
      ) : state.kind === "error" ? (
        <span className="text-muted-foreground">
          Couldn&rsquo;t read the repeat history for this price group.
        </span>
      ) : state.kind !== "ready" ? (
        <span className="text-muted-foreground">
          Checking the last six months&rsquo; repeats…
        </span>
      ) : (
        <>
          <span className="text-muted-foreground">
            At these limits, over the last {state.calibration.window_months}{" "}
            months&rsquo; repeats ({state.calibration.pairs.toLocaleString()}{" "}
            quotes with a prior at ±10% quantity):
          </span>{" "}
          <span className="font-medium tabular-nums">
            {state.calibration.pass_pct}% pass
          </span>
          {" · "}
          <span className="font-medium tabular-nums text-amber-700 dark:text-amber-400">
            {state.calibration.approval_pct}% need approval
          </span>
          {" · "}
          <span className="font-medium tabular-nums text-red-700 dark:text-red-400">
            {state.calibration.block_pct}% would block
          </span>
          <span className="ml-2 text-xs text-muted-foreground">
            ({state.calibration.identical_pct}% were priced identically to last
            time)
          </span>
        </>
      )}
    </div>
  );
}

// A directional threshold: separate upward and downward magnitudes, so a
// returning customer's price can be held tighter one way than the other. Labelled
// rows rather than ± so the two directions are unambiguous.
function DirectionalLimit({
  up,
  down,
  onUp,
  onDown,
}: {
  up: string;
  down: string;
  onUp: (v: string) => void;
  onDown: (v: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <span className="w-16 text-xs text-muted-foreground">Increase</span>
        <PctTextCell value={up} onChange={onUp} className="w-16" />
      </div>
      <div className="flex items-center gap-2">
        <span className="w-16 text-xs text-muted-foreground">Decrease</span>
        <PctTextCell value={down} onChange={onDown} className="w-16" />
      </div>
    </div>
  );
}

function CarriagePanel({
  draft,
  onEdit,
}: {
  draft: PriceGroup;
  onEdit: (d: PriceGroup) => void;
}) {
  const c = draft.carriage;
  const setEnabled = (on: boolean) =>
    onEdit({
      ...draft,
      carriage: on ? (c ?? { markupPct: 0 }) : null,
    });
  return (
    <PanelShell
      title="Carriage"
      hint="The markup we add to a courier-quoted carriage cost when a job ships to a chosen delivery address. 0% passes the courier's own price straight through."
      action={<EnableSwitch checked={c != null} onCheckedChange={setEnabled} />}
    >
      {c == null ? (
        <div className="text-sm text-muted-foreground">
          Off. Carriage is quoted at cost, with no markup.
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between gap-3 max-w-sm">
            <Label className="font-normal text-muted-foreground">
              Carriage markup
            </Label>
            <PctInput
              min={0}
              value={c.markupPct}
              onChange={(v) =>
                onEdit({
                  ...draft,
                  carriage: { ...c, markupPct: v ?? 0 },
                })
              }
            />
          </div>
          <div className="rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
            Applies only to carriage quoted against a chosen address. An
            operator-entered manual carriage price is already the figure the
            customer will be charged, and is never marked up.
          </div>
        </>
      )}
    </PanelShell>
  );
}

function RowDelete({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="text-muted-foreground hover:text-destructive"
      aria-label="Remove"
    >
      <Trash2 className="size-3.5" />
    </button>
  );
}

function AddButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
    >
      <Plus className="size-3.5" /> {label}
    </button>
  );
}
